import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'

import { type TeamExternalIntegrationGcp } from 'types/api/status-checks/status-check'

export enum GcpAuthRadioChoice {
  Existing = 'existing',
  New = 'new'
}

export interface GcpAuthenticationFormValuesNew {
  serviceAccountId: string
}

export interface GcpAuthenticationFormValuesExisting
  extends GcpAuthenticationFormValuesNew {
  authRadioChoice: GcpAuthRadioChoice | undefined
  domain: string
}

/*
 * This returns the regex capture group which correlates with the integration `domain` field (when uppercased).
 */
export function getGcpServiceAccountDomain(serviceAccountEmail: string) {
  return GCP_SERVICE_ACCOUNT_ID_VALIDATION.regex.exec(serviceAccountEmail)?.[1]
}

export function doesGcpServiceAccountMatchWithDomain(
  serviceAccountEmail: string,
  domain: string
) {
  const serviceAccountDomain =
    getGcpServiceAccountDomain(serviceAccountEmail)?.toUpperCase()

  return serviceAccountDomain ? serviceAccountDomain === domain : false
}

export function hasMatchedServiceAccountDomain(
  serviceAccountEmail: string,
  domains: string[]
) {
  return domains.some((domain) =>
    doesGcpServiceAccountMatchWithDomain(serviceAccountEmail, domain)
  )
}

const GCP_DOMAIN_NOT_SET_MESSAGE = 'Domain not set'
const GCP_DOMAIN_NOT_VALID_MESSAGE = 'Domain not valid'

const GCP_SERVICE_ACCOUNT_ID_VALIDATION = {
  message:
    'Must be a valid service account email (e.g. my-sa@my-project.iam.gserviceaccount.com)',
  regex: /^[a-z0-9._-]+@([a-z0-9-]+)\.iam\.gserviceaccount\.com$/
}

const GCP_SERVICE_ACCOUNT_ID_DUPLICATION = {
  message: 'Cannot be an existing Service Account ID or project ID (domain)',
  /* Id is duplicated if it matches any of the existing domains */
  test: (value: string | undefined, domains: string[]) =>
    !value ||
    domains.length === 0 ||
    !hasMatchedServiceAccountDomain(value, domains)
}

export const SERVICE_ACCOUNT_ID_FIELD_PROPS = {
  'data-testid': 'gcp-authentication-service-account-id-input',
  label: 'Service Account ID (email)',
  sx: { width: '100%', maxWidth: '100%', marginTop: 1 }
} as const

/**
 * Builds the Yup schema for GCP authentication form validation.
 *
 * `isNewAuthOnly` is required because there are two different form shapes:
 * - `true`: form only allows creating a new authentication.
 * - `false`: form supports selecting existing or creating new authentication.
 *   GcpAuthRadioChoice is expected to be present in the consuming form.
 */
function getGcpAuthenticationFormSchema({
  integrations,
  isNewAuthOnly
}: {
  integrations: TeamExternalIntegrationGcp[]
  isNewAuthOnly: boolean
}) {
  const domains = integrations.map(({ domain }) => domain)

  const newServiceAccountIdSchema = yup
    .string()
    .required('Service account email address is required')
    .matches(
      GCP_SERVICE_ACCOUNT_ID_VALIDATION.regex,
      GCP_SERVICE_ACCOUNT_ID_VALIDATION.message
    )
    .test(
      'not-duplicate',
      GCP_SERVICE_ACCOUNT_ID_DUPLICATION.message,
      (value) => GCP_SERVICE_ACCOUNT_ID_DUPLICATION.test(value, domains)
    )

  if (isNewAuthOnly) {
    return yup.object({
      serviceAccountId: newServiceAccountIdSchema
    })
  }

  return yup.object({
    authRadioChoice: yup
      .string()
      .oneOf(Object.values(GcpAuthRadioChoice))
      .nullable(),
    domain: yup.string().when('authRadioChoice', {
      is: GcpAuthRadioChoice.Existing,
      then: (schema) =>
        schema
          .oneOf(domains, GCP_DOMAIN_NOT_VALID_MESSAGE)
          .required(GCP_DOMAIN_NOT_SET_MESSAGE)
    }),
    serviceAccountId: yup.string().when('authRadioChoice', {
      is: (radioChoice: GcpAuthRadioChoice | undefined) => {
        return radioChoice !== GcpAuthRadioChoice.Existing
      },
      then: newServiceAccountIdSchema
    })
  })
}

/**
 * Provides form initializers for when only creating new authentication is allowed.
 */
export function getGcpAuthenticationFormInitializersNew(
  integrations: TeamExternalIntegrationGcp[]
) {
  return {
    defaultValues: {
      serviceAccountId: ''
    } satisfies GcpAuthenticationFormValuesNew,
    resolver: yupResolver(
      getGcpAuthenticationFormSchema({
        integrations,
        isNewAuthOnly: true
      })
    )
  }
}

/**
 * Provides form initializers for when the option to add an existing authentication is available.
 * `existingDomain` should be provided only if we're editing an existing authentication and
 * want to pre-populate the form with that domain.
 */
export function getGcpAuthenticationFormInitializersExisting({
  existingDomain,
  integrations
}: {
  existingDomain?: string
  integrations: TeamExternalIntegrationGcp[]
}) {
  const isExistingDomainInIntegrations = existingDomain
    ? integrations.some(({ domain }) => domain === existingDomain)
    : false

  const defaultValues: GcpAuthenticationFormValuesExisting = integrations.length
    ? {
        authRadioChoice: GcpAuthRadioChoice.Existing,
        domain:
          isExistingDomainInIntegrations && existingDomain
            ? existingDomain
            : integrations[0]?.domain ?? '',
        serviceAccountId: ''
      }
    : {
        authRadioChoice: undefined,
        domain: '',
        serviceAccountId: ''
      }

  return {
    defaultValues,
    resolver: yupResolver(
      getGcpAuthenticationFormSchema({
        integrations,
        isNewAuthOnly: false
      })
    )
  }
}
